import { Button, Card, Flex, HStack, Icon, Text, Textarea } from '@chakra-ui/react';
import { FiCalendar, FiClock, FiEdit, FiMapPin, FiUser, FiUsers } from 'react-icons/fi';
import { formatLessonCodeSummary } from '@opanuj/shared/utils';

const actionConfig = {
	add: {
		label: 'Dodaj do listy',
		text: 'Zapisz się',
		palette: 'orange',
	},
	remove: {
		label: 'Usuń z listy',
		text: 'Usuń',
		palette: 'grey',
	},
	none: null,
} as const;

export const SlotCard = (props: any) => {
	const { slot, actionType = 'add', showSlotCode = false, onNoteChange } = props;
	const slotData = {
		code: slot.code ?? slot.slotCode ?? '',
		notes: slot.notes ?? slot.note ?? '',
		dayFull: slot.dayFull ?? slot.day ?? '',
		time: slot.time ?? slot.start ?? '',
		duration: slot.duration ?? 0,
		locationFull: slot.locationFull ?? slot.location ?? '',
		teacher: slot.teacher ?? '',
		capacity: slot.capacity ?? null,
	};
	const slotCodeSummary = [formatLessonCodeSummary(slotData.code)];
	// const slotCodeSummary = [formatLessonCodeSummary(slotData.code), formatLessonStudentInfo(slot.student)].filter(Boolean).join(' • ');
	const availableSeats =
		slotData.capacity && slotData.capacity.limit > 1
			? Math.max(slotData.capacity.limit - slotData.capacity.current, 0)
			: null;
	const hasNotes = Boolean(slotData.notes?.trim());
	const canEditNotes = Boolean(onNoteChange) && actionType !== 'none';
	const action = actionType === 'none' ? null : (actionConfig[actionType as keyof typeof actionConfig] ?? actionConfig.add);

	const renderMeta = (icon: typeof FiCalendar, label: string) => (
		<HStack gap={1.5} align="center" minW={0}>
			<Icon as={icon} color="gray.500" boxSize={3.5} />
			<Text fontSize="sm" lineHeight="shorter">{label}</Text>
		</HStack>
	);

	return (
		<Card.Root
			size="sm"
			my={1.5}
			key={props.index}
			borderRadius={{ base: '4px', lg: '0' }}
			display="flex"
			flexDirection='row'
			overflow="hidden"
			alignItems="stretch"
			justifyContent="space-between"
			borderWidth={{ base: '1px', lg: '0' }}
			borderColor="orange.200"
			boxShadow="none"
			borderBottomWidth={{ base: '1px', lg: '1px' }}
		>
			<Card.Body p={2} flex="1" minW={0}>
				{showSlotCode && slotCodeSummary && (
					<Text fontSize="sm" fontWeight="semibold" lineHeight="shorter" mb={2.5} overflowWrap="anywhere">
						{slotCodeSummary}
					</Text>
				)}
				<Flex
					display={{ base: 'none', lg: 'flex' }}
					direction={'row'}
					gap={2}
					wrap='wrap'
					align='flex-start'
					justify='flex-start'
					minW={0}
					w="100%"
				>
					{slotData.dayFull && renderMeta(FiCalendar, `${slotData.dayFull}, ${slotData.time}`)}
					{slotData.duration > 0 && renderMeta(FiClock, `${slotData.duration} min`)}
					{slotData.locationFull && renderMeta(FiMapPin, slotData.locationFull)}
					{slotData.teacher && renderMeta(FiUser, slotData.teacher)}
					{availableSeats !== null && (
						<HStack gap={2} align="center" minW={0}>
							<Icon as={FiUsers} color="gray.500" boxSize={3.5} />
							<Text fontSize="sm" lineHeight="shorter">
								{`dostępne miejsca: ${availableSeats}/${slotData.capacity.limit}`}
							</Text>
						</HStack>
					)}
				</Flex>
				{canEditNotes ? (
					<Flex align="center" gap={2} mt={1} minW={0}>
						<Icon as={FiEdit} color="gray.500" boxSize={3.5} mt={1.5} flexShrink={0} alignSelf='flex-start'/>
						<Textarea
							value={slotData.notes ?? ''}
							py={1}
							onChange={(event) => onNoteChange(event.target.value)}
							size="sm"
							rows={1}
							pl={0}
							resize="none"
							variant="subtle"
							placeholder="dodatkowe informacje"
						/>
					</Flex>
				) : hasNotes ? (
					<Flex align="center" gap={2} mt={1} minW={0}>
						<Icon as={FiEdit} color="gray.500" boxSize={3.5} mt={1.5} flexShrink={0} alignSelf='flex-start'/>
						<Text fontSize="sm" color="gray.700" lineHeight="shorter" whiteSpace="normal" overflowWrap="anywhere">
							{slotData.notes}
						</Text>
					</Flex>
				) : null}
				<Flex display={{ base: 'flex', lg: 'none' }} direction="column" gap={1.5} minW={0} w="100%">
					<Flex gap={2} wrap="wrap" align="center" minW={0}>
						{slotData.dayFull && renderMeta(FiCalendar, `${slotData.dayFull}, ${slotData.time}`)}
						{slotData.duration > 0 && renderMeta(FiClock, `${slotData.duration} min`)}
					</Flex>
					<Flex gap={2} wrap="wrap" align="center" minW={0}>
						{slotData.locationFull && renderMeta(FiMapPin, slotData.locationFull)}
						{slotData.teacher && renderMeta(FiUser, slotData.teacher)}
					</Flex>
					{availableSeats !== null && (
						<HStack gap={2} align="center" minW={0}>
							<Icon as={FiUsers} color="gray.500" boxSize={3.5} />
							<Text fontSize="sm" lineHeight="shorter">
								{`dostępne miejsca: ${availableSeats}/${slotData.capacity.limit}`}
							</Text>
						</HStack>
					)}
					{canEditNotes ? (
						<Flex align="flex-start" gap={1.5} minW={0}>
							<Icon as={FiEdit} color="gray.500" boxSize={3.5} mt={0.5} flexShrink={0} />
							<Textarea
								value={slotData.notes ?? ''}
								variant="flushed"
								onChange={(event) => onNoteChange(event.target.value)}
								size="sm"
								rows={1}
								placeholder="Dodaj notatkę do zajęć"
							/>
						</Flex>
					) : hasNotes ? (
						<Flex align="flex-start" gap={1.5} minW={0}>
							<Icon as={FiEdit} color="gray.500" boxSize={3.5} mt={0.5} flexShrink={0} />
							<Text fontSize="sm" color="gray.700" lineHeight="shorter" whiteSpace="normal" overflowWrap="anywhere">
								{slotData.notes}
							</Text>
						</Flex>
					) : null}
				</Flex>
			</Card.Body>
			{action && (
				<Button
					alignSelf="stretch"
					colorPalette={action.palette}
					variant="subtle"
					size="sm"
					onClick={props.onClick}
					aria-label={action.label}
					borderRadius={{ base: '0', lg: '4px 4px 0 0' }}
					ml={{ base: 0, sm: 'auto' }}
					h="auto"
					minW="2.75rem"
					px={2}
					flexShrink={0}
				>
					{action.text}
				</Button>
			)}
		</Card.Root>
	);
};
